import { useRef } from "react";
import { useReactiveVar } from "@apollo/client";
import {
  ApplicationBundleTypeEnum,
  ApplicationMainModelFileTypeEnum,
  ExperimentBundleSubType,
} from "../../../../../generated/graphql";
import { mobileAppModel } from "../../state";
import { getBundleVariant } from "../../utils";
import { BundleNames, PLATFORM_TO_BUNDLE_MAP } from "../../constants";
import { CurrentBundleContainer } from "../../../MobileAppModal.style";
import { Collapse, IconButton, Typography } from "@material-ui/core";
import { Delete } from "@material-ui/icons";
import {
  BundleLink,
  ButtonCustom,
  ImportFileUpload,
} from "../../../../shared/Style/Style";
import { BundleDataType, ExperimentFieldTypes } from "../../types";

export const AppModelBundles = () => {
  const resourceInputIos = useRef<HTMLInputElement | null>(null);
  const resourceInputAndroid = useRef<HTMLInputElement | null>(null);
  const resourceInputVr = useRef<HTMLInputElement | null>(null);
  const resourceInputWebGl = useRef<HTMLInputElement | null>(null);
  const resourceInputAndroidBoard = useRef<HTMLInputElement | null>(null);
  const resourceInputWindowsBoard = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputIos = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputAndroid = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputWebGl = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputVr = useRef<HTMLInputElement | null>(null);
  const resourcesManifestInputAndroidBoard = useRef<HTMLInputElement | null>(
    null,
  );
  const resourcesManifestInputWindowsBoard = useRef<HTMLInputElement | null>(
    null,
  );
  const isLoading = useReactiveVar(mobileAppModel.updateLoading);
  const model = useReactiveVar(mobileAppModel.model);
  const activePlatform = useReactiveVar(mobileAppModel.activePlatform);

  if (!model) return null;

  const { id, bundles, bundleType, modelBundle, contentType } = model;

  const bundlesData = [
    {
      name: BundleNames.Ios,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      currentModelLink: modelBundle?.iosBundleUrl,
    },
    {
      name: BundleNames.WebGL,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      currentModelLink: modelBundle?.webGlBundleUrl,
    },
    {
      name: BundleNames.Android,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      currentModelLink: modelBundle?.androidBundleUrl,
    },
    {
      name: BundleNames.Vr,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      currentModelLink: modelBundle?.vrBundleUrl,
    },
    {
      name: BundleNames.AndroidBoardBundle,
      type: ExperimentFieldTypes.Bundle,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      currentModelLink: modelBundle?.interactiveWhiteboardAndroidBundleUrl,
    },
    {
      name: BundleNames.WindowsBoardBundle,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      currentModelLink: modelBundle?.interactiveWhiteboardWindowsBundleUrl,
      type: ExperimentFieldTypes.Bundle,
    },
    {
      name: BundleNames.IosManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      currentModelLink: modelBundle?.iosManifestUrl,
    },
    {
      name: BundleNames.WebGlManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      currentModelLink: modelBundle?.webGlManifestUrl,
    },
    {
      name: BundleNames.AndroidManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      currentModelLink: modelBundle?.androidManifestUrl,
    },
    {
      name: BundleNames.VrManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      currentModelLink: modelBundle?.vrManifestUrl,
    },
    {
      name: BundleNames.AndroidBoardManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      currentModelLink: modelBundle?.interactiveWhiteboardAndroidManifestUrl,
    },
    {
      name: BundleNames.WindowsBoardManifest,
      type: ExperimentFieldTypes.Manifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      currentModelLink:
        model.modelBundle?.interactiveWhiteboardWindowsManifestUrl,
    },
    {
      name: BundleNames.IosResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      inputRef: resourceInputIos,
    },
    {
      name: BundleNames.IosResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Ios,
      inputRef: resourcesManifestInputIos,
    },
    {
      name: BundleNames.AndroidResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      inputRef: resourceInputAndroid,
    },
    {
      name: BundleNames.AndroidResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Android,
      inputRef: resourcesManifestInputAndroid,
    },
    {
      name: BundleNames.WebGlResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      inputRef: resourceInputWebGl,
    },
    {
      name: BundleNames.WebGlResourceManifests,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.WebGl,
      inputRef: resourcesManifestInputWebGl,
    },
    {
      name: BundleNames.VrResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      inputRef: resourceInputVr,
    },
    {
      name: BundleNames.VrResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.Vr,
      inputRef: resourcesManifestInputVr,
    },
    {
      name: BundleNames.AndroidBoardResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      inputRef: resourceInputAndroidBoard,
    },
    {
      name: BundleNames.WindowsBoardResources,
      type: ExperimentBundleSubType.Resources,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      inputRef: resourceInputWindowsBoard,
    },
    {
      name: BundleNames.AndroidBoardResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardAndroid,
      inputRef: resourcesManifestInputAndroidBoard,
    },
    {
      name: BundleNames.WindowsBoardResourcesManifest,
      type: ExperimentBundleSubType.ResourcesManifest,
      fileType: ApplicationMainModelFileTypeEnum.InteractiveWhiteboardWindows,
      inputRef: resourcesManifestInputWindowsBoard,
    },
  ];

  const visibleBundles = PLATFORM_TO_BUNDLE_MAP[activePlatform] || [];

  const filteredBundlesData = bundlesData.filter((bundle) =>
    visibleBundles.includes(bundle.name),
  );

  const renderCurrentBundle = ({
    name,
    currentModelLink,
    type,
    fileType,
    inputRef,
  }: BundleDataType) => {
    const currentBundle = bundles.find((bundle) => bundle.type === fileType);
    const bundleLink =
      getBundleVariant(currentBundle, type) || currentModelLink;
    const isBundleOrManifest =
      type === ExperimentFieldTypes.Bundle ||
      type === ExperimentFieldTypes.Manifest;

    if (!bundleLink && isBundleOrManifest) return null;

    return (
      <>
        {!currentBundle?.id && type === ExperimentFieldTypes.Bundle && (
          <Typography
            variant="body2"
            color="error"
            style={{ maxWidth: "480px", marginTop: "16px" }}
          >
            Оновлено модель бандлу. Щоб завантажити файли локалізації, спочатку
            збережіть оновлену модель, натиснувши кнопку "Зберегти"
          </Typography>
        )}
        <CurrentBundleContainer key={name}>
          <BundleLink href={bundleLink}>{name}</BundleLink>

          {!!bundleLink ? (
            <span>
              {!isBundleOrManifest && (
                <IconButton
                  disabled={isLoading || !currentBundle?.id}
                  onClick={() =>
                    mobileAppModel.deleteBundleFile(
                      id,
                      currentBundle?.id,
                      type as ExperimentBundleSubType,
                    )
                  }
                >
                  <Delete color="secondary" />
                </IconButton>
              )}
            </span>
          ) : (
            <ButtonCustom
              disabled={isLoading || !currentBundle?.id}
              onClick={() => inputRef.current?.click()}
            >
              <span>Прикріпити</span>
              <ImportFileUpload
                type="file"
                ref={inputRef}
                onChange={({ target: { files } }) => {
                  if (!files?.[0] || !currentBundle?.id) return;
                  mobileAppModel.updateModalFile({
                    modelId: id,
                    id: currentBundle.id,
                    ...(type === ExperimentBundleSubType.Resources
                      ? { resources: files[0] }
                      : { resourcesManifest: files[0] }),
                  });
                }}
              />
            </ButtonCustom>
          )}
        </CurrentBundleContainer>
      </>
    );
  };

  return (
    <>
      <Collapse
        in={
          bundleType === ApplicationBundleTypeEnum.Bundle && !!contentType?.name
        }
        collapsedSize="0px"
      >
        {filteredBundlesData.map((data) => renderCurrentBundle({ ...data }))}
      </Collapse>
    </>
  );
};
